import React, { useRef, useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useGameState } from './hooks/useGameState';
import { useGameAudio } from './hooks/useGameAudio';
import { useBossBattle } from './hooks/useBossBattle';
import { useBoardInteractions } from './hooks/useBoardInteractions';

import { TopBar } from './components/TopBar';
import { Board } from './components/Board';
import { Library } from './components/Library';
import { NewDiscoveryPopup } from './components/NewDiscoveryPopup';
import { EraUnlockPopup } from './components/EraUnlockPopup';
import { MainMenu } from './components/MainMenu';
import { PauseMenu } from './components/PauseMenu';
import { AudioSettingsPopup } from './components/AudioSettingsPopup';
import { ElementDetailPopup } from './components/ElementDetailPopup';
import { CollectionUnlockPopup } from './components/CollectionUnlockPopup';
import { CollectionsMenu } from './components/CollectionsMenu';
import { HintPopup } from './components/HintPopup';
import { DeckBuilder } from './components/DeckBuilder';
import { TutorialPopup } from './components/TutorialPopup';
import { CreditsPopup } from './components/CreditsPopup';
import { CONSTANTS } from './data';
import { SplashScreen } from './components/SplashScreen';

type Screen = 'main_menu' | 'game';

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const splashConfig = CONSTANTS.SPLASH;
    if (!splashConfig) {
      setIsLoading(false);
      return;
    }
    // Total duration is 3 seconds as requested
    const totalDuration = 3000;
    const timer = setTimeout(() => {
        setIsLoading(false);
    }, totalDuration);

    return () => clearTimeout(timer);
  }, []);

  const gameState = useGameState();
  const {
    discovered,
    unlockedEras,
    instances,
    effects,
    newDiscovery,
    newEraUnlock,
    eraToAdvanceTo,
    totalElements,
    currentEraKey,
    currentEraName,
    elementsByEra,
    completedCollections,
    completedTutorials,
    playerAvatar,
    playerDeck,
    newCollectionUnlock,
    unsuccessfulCombos,
    hint,
    tutorialState,
    setNewCollectionUnlock,
    setPlayerAvatar,
    setPlayerDeck,
    setNewDiscovery,
    setNewEraUnlock,
    setEraToAdvanceTo,
    setHint,
    getHint,
    completeCombination,
    resetBoard: resetBoardState,
    resetProgress: resetProgressState,
    startTutorial,
    advanceTutorial,
    completeTutorial,
  } = gameState;

  const { 
    musicEnabled, setMusicEnabled, 
    sfxEnabled, setSfxEnabled, 
    musicVolume, setMusicVolume,
    sfxVolume, setSfxVolume,
    playSoundEffect 
  } = useGameAudio(currentEraKey);

  const boardRef = useRef<HTMLDivElement | null>(null);
  const libraryRef = useRef<HTMLDivElement | null>(null);
  
  const [showPauseMenu, setShowPauseMenu] = useState<boolean>(false);
  const [showAudioSettings, setShowAudioSettings] = useState<boolean>(false);
  const [showCollectionsMenu, setShowCollectionsMenu] = useState<boolean>(false);
  const [showDeckBuilder, setShowDeckBuilder] = useState<boolean>(false);
  const [showCredits, setShowCredits] = useState<boolean>(false);
  const [isDeckBuilderForced, setIsDeckBuilderForced] = useState<boolean>(false);
  const [isDeckBuilderFromDefeat, setIsDeckBuilderFromDefeat] = useState<boolean>(false);
  const [screen, setScreen] = useState<Screen>('main_menu');
  const [elementDetailKey, setElementDetailKey] = useState<string | null>(null);
  const [pendingEraAdvancement, setPendingEraAdvancement] = useState<any>(null);

  const returnToMainMenu = useCallback(() => {
    setShowPauseMenu(false);
    setScreen('main_menu');
  }, []);

  const cancelAdvanceAndReturnToMainMenu = useCallback(() => {
    setEraToAdvanceTo(null);
    setPendingEraAdvancement(null);
    returnToMainMenu();
  }, [setEraToAdvanceTo, returnToMainMenu]);

  const handleEraUnlockClose = useCallback(() => {
    if (newEraUnlock?.key === 'bronze_age' && !completedTutorials.includes('deck_builder_tutorial')) {
      setShowDeckBuilder(true);
      startTutorial('deck_builder_tutorial');
    }
    setNewEraUnlock(null);
  }, [newEraUnlock, completedTutorials, startTutorial, setNewEraUnlock]);

  const bossBattle = useBossBattle({ ...gameState, playSoundEffect, cancelAdvanceAndReturnToMainMenu, isDeckBuilderForced, startTutorial, advanceTutorial });
  const boardInteractions = useBoardInteractions({ ...gameState, boardRef, libraryRef, playSoundEffect, gamePhase: bossBattle.gamePhase, advanceTutorial });

  useEffect(() => {
    if (screen === 'game' && discovered.length === CONSTANTS.startingElements.length && !completedTutorials.includes('first_combination')) {
      startTutorial('first_combination');
    }
  }, [screen, discovered.length, completedTutorials, startTutorial]);

  useEffect(() => {
    if (eraToAdvanceTo) {
      const deckBuilderUnlocked = unlockedEras.includes('bronze_age');
      const isSecondBoss = eraToAdvanceTo.key === 'classical_age';
      if (deckBuilderUnlocked && isSecondBoss && playerDeck.length === 0) {
        setPendingEraAdvancement(eraToAdvanceTo);
        setShowDeckBuilder(true);
        setIsDeckBuilderForced(true);
      }
    }
  }, [eraToAdvanceTo, unlockedEras, playerDeck]);

  useEffect(() => {
    if (newDiscovery) {
      playSoundEffect('new_discovery');
    }
  }, [newDiscovery, playSoundEffect]);

  useEffect(() => {
    if (!newDiscovery && (newEraUnlock || eraToAdvanceTo)) {
      playSoundEffect('new_era');
    }
  }, [newEraUnlock, newDiscovery, eraToAdvanceTo, playSoundEffect]);

  const resetBoard = useCallback(() => {
    resetBoardState();
    setShowPauseMenu(false);
  }, [resetBoardState]);

  const resetProgressAndStart = useCallback(() => {
    resetProgressState();
    bossBattle.resetBossBattle();
    setShowPauseMenu(false);
  }, [resetProgressState, bossBattle]);

  const openAudioSettings = useCallback(() => {
    setShowPauseMenu(false);
    setShowAudioSettings(true);
  }, []);

  const openCollectionsMenu = useCallback(() => {
    setShowPauseMenu(false);
    setShowCollectionsMenu(true);
  }, []);

  const handleOpenDeckBuilderFromDefeat = useCallback(() => {
    setIsDeckBuilderFromDefeat(true);
    setShowDeckBuilder(true);
  }, []);

  const deckBuilderUnlocked = unlockedEras.includes('bronze_age');

  return (
    <div className="w-screen h-screen overflow-hidden bg-neutral-900 text-white">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <SplashScreen key="splash" />
        ) : (
          <motion.div
            key="game"
            className="w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <AnimatePresence>
              {screen === 'main_menu' && (
                <MainMenu
                  onStartGame={() => setScreen('game')}
                  onOpenAudioSettings={() => setShowAudioSettings(true)}
                  onOpenCollections={() => setShowCollectionsMenu(true)}
                  onOpenCredits={() => setShowCredits(true)}
                />
              )}
            </AnimatePresence>

            {screen === 'game' && (
              <>
                <TopBar
                  currentEraName={currentEraName}
                  discoveredCount={discovered.length}
                  totalElements={totalElements}
                  onResetBoard={resetBoard}
                  onToggleMainMenu={() => setShowPauseMenu(v => !v)}
                  gamePhase={bossBattle.gamePhase}
                  playerScore={bossBattle.playerScore}
                  bossScore={bossBattle.bossScore}
                  currentBoss={bossBattle.currentBoss}
                  statusText={bossBattle.statusText}
                  playerAvatar={playerAvatar}
                />

                <div className="w-full h-[calc(100vh-56px)] flex">
                  <Board
                    boardRef={boardRef}
                    instances={instances}
                    effects={effects}
                    paletteDrag={boardInteractions.paletteDrag}
                    highlightedIds={boardInteractions.highlightedIds}
                    dragState={boardInteractions.drag}
                    currentEraKey={currentEraKey}
                    currentEraName={currentEraName}
                    onPointerMove={boardInteractions.onPointerMove}
                    onPointerUp={boardInteractions.onPointerUp}
                    onPointerCancel={boardInteractions.onPointerCancel}
                    onPointerDownInstance={boardInteractions.onPointerDown}
                    onBoardDragOver={boardInteractions.onBoardDragOver}
                    onBoardDrop={boardInteractions.onBoardDrop}
                    onBoardDragLeave={boardInteractions.onBoardDragLeave}
                    onEffectAnimationComplete={completeCombination}
                    gamePhase={bossBattle.gamePhase}
                    playerFieldCards={bossBattle.playerFieldCards}
                    bossFieldCards={bossBattle.bossFieldCards}
                    roundResult={bossBattle.roundResult}
                    currentBoss={bossBattle.currentBoss}
                    onStartBossBattle={bossBattle.startBossBattle}
                    onNextRound={bossBattle.handleNextRound}
                    onBossBattleClose={bossBattle.handleBossBattleClose}
                    dialogueIndex={bossBattle.dialogueIndex}
                    onNextDialogue={bossBattle.handleNextDialogue}
                    onPlayCardOnField={bossBattle.handlePlayCardOnField}
                    onCombineOnField={bossBattle.handleCombineOnField}
                    playerHand={bossBattle.playerHand}
                    bossHand={bossBattle.bossHand}
                    playerAvatar={playerAvatar}
                    onRetryBattle={bossBattle.handleRetryBattle}
                    onReturnToMainMenuFromDefeat={bossBattle.handleReturnToMainMenuFromDefeat}
                    onOpenDeckBuilderFromDefeat={handleOpenDeckBuilderFromDefeat}
                    unsuccessfulCombos={unsuccessfulCombos}
                    onGetHint={getHint}
                    deckBuilderUnlocked={deckBuilderUnlocked}
                    onOpenDeckBuilder={() => setShowDeckBuilder(true)}
                    tutorialState={tutorialState}
                  />

                  <Library
                    libraryRef={libraryRef}
                    overLibrary={boardInteractions.overLibrary}
                    elementsByEra={elementsByEra}
                    discovered={discovered}
                    onShowDetails={setElementDetailKey}
                    gamePhase={bossBattle.gamePhase}
                    playerHand={bossBattle.playerHand}
                    battlePlayerDeck={bossBattle.battlePlayerDeck}
                    onPassTurn={bossBattle.handlePassTurn}
                    isPlayerTurn={bossBattle.currentTurn === 'player'}
                    playerHasPassed={bossBattle.playerPassed}
                    bossHand={bossBattle.bossHand}
                    currentEraKey={currentEraKey}
                    tutorialState={tutorialState}
                  />
                </div>
              </>
            )}

            <AnimatePresence>
              {newDiscovery && (
                <NewDiscoveryPopup
                  discovery={newDiscovery}
                  onClose={() => setNewDiscovery(null)}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {!newDiscovery && !eraToAdvanceTo && newEraUnlock && (
                <EraUnlockPopup
                  eraUnlock={newEraUnlock}
                  onClose={handleEraUnlockClose}
                />
              )}
            </AnimatePresence>
            
            <AnimatePresence>
              {newCollectionUnlock && (
                <CollectionUnlockPopup
                  collection={newCollectionUnlock}
                  onClose={() => setNewCollectionUnlock(null)}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showPauseMenu && (
                <PauseMenu
                  onContinue={() => setShowPauseMenu(false)}
                  onNewGame={resetProgressAndStart}
                  onOpenAudioSettings={openAudioSettings}
                  onOpenCollections={openCollectionsMenu}
                  onReturnToMainMenu={returnToMainMenu}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showAudioSettings && (
                <AudioSettingsPopup
                  onClose={() => setShowAudioSettings(false)}
                  musicEnabled={musicEnabled}
                  onToggleMusic={() => setMusicEnabled(v => !v)}
                  musicVolume={musicVolume}
                  onMusicVolumeChange={setMusicVolume}
                  sfxEnabled={sfxEnabled}
                  onToggleSfx={() => setSfxEnabled(v => !v)}
                  sfxVolume={sfxVolume}
                  onSfxVolumeChange={setSfxVolume}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showCollectionsMenu && (
                <CollectionsMenu
                  onClose={() => setShowCollectionsMenu(false)}
                  discovered={discovered}
                  completedCollections={completedCollections}
                  playerAvatar={playerAvatar}
                  onSelectAvatar={setPlayerAvatar}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showCredits && (
                <CreditsPopup
                  onClose={() => setShowCredits(false)}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showDeckBuilder && (
                <DeckBuilder
                  onClose={(saved) => {
                    setShowDeckBuilder(false);
                    if (tutorialState.active === 'deck_builder_tutorial') {
                      completeTutorial();
                    }
                    if (isDeckBuilderForced) {
                      if (saved && pendingEraAdvancement) {
                        // Deck was saved and we have a pending era advancement
                        // The boss battle should now initialize with the new deck
                        setEraToAdvanceTo(pendingEraAdvancement);
                      } else if (!saved) {
                        // Deck was not saved, cancel the advancement
                        setEraToAdvanceTo(null);
                      }
                      setIsDeckBuilderForced(false);
                      setPendingEraAdvancement(null);
                    }
                    if (isDeckBuilderFromDefeat) {
                      setIsDeckBuilderFromDefeat(false);
                      // Don't change any boss battle state, just return to defeat screen
                    }
                  }}
                  discovered={discovered}
                  initialDeck={playerDeck}
                  onSaveDeck={setPlayerDeck}
                  currentEraKey={currentEraKey}
                  unlockedEras={unlockedEras}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {elementDetailKey && (
                <ElementDetailPopup
                  elementKey={elementDetailKey}
                  discovered={discovered}
                  onClose={() => setElementDetailKey(null)}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {hint && (
                <HintPopup
                  hint={hint}
                  onClose={() => setHint(null)}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {tutorialState.active && (
                <TutorialPopup
                  tutorialState={tutorialState}
                  onNext={advanceTutorial}
                  onClose={completeTutorial}
                />
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
